import { Clock, Euro, Mail, Phone, CreditCard, CalendarCheck, Video, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useT, useAnchorHref, useLocalePath } from "@/i18n";

const Pricing = () => {
  const anchor = useAnchorHref();
  const lp = useLocalePath();
  const t = useT(
    {
      eyebrow: "Terminvereinbarung",
      heading: "Termine & Preise",
      payment: "Bankomatkartenzahlung möglich",
      cardTitle: "Beratungstermin",
      priceNote: "inkl. 20% MwSt.",
      duration: "ca. 60 Minuten",
      zoomNote: "Erst- und Folgetermine vor Ort können Sie online buchen. Zoom-Termine sind nach individueller persönlicher Vereinbarung möglich – bitte kontaktieren Sie mich per E-Mail oder Telefon. Ein Ersttermin sollte grundsätzlich in der Praxis stattfinden. Ist dies nicht möglich, besprechen wir gerne gemeinsam eine passende Lösung.",
      includesTitle: "Was Sie im Erstgespräch erwartet",
      includes: [
        "Ausführliches Gespräch: Beschwerdeverlauf, bisherige Therapien, Symptome, etc.",
        "Gemeinsame Durchsicht Ihrer vorhandenen Befunde (Labor, Stuhlanalysen, Atemtests, Arztbriefe)",
        "Einordnung möglicher Zusammenhänge zwischen den einzelnen Beschwerden",
        "Besprechung der nächsten sinnvollen Schritte",
        "Völlige Kostentransparenz: nur so viel wie nötig, nicht so viel wie möglich.",
      ],
      bookCta: "Termin online buchen",
      emailCta: "Per E-Mail anfragen",
      phoneCta: "Anrufen",
    },
    {
      eyebrow: "Booking an appointment",
      heading: "Appointments & Fees",
      payment: "Debit card payment accepted",
      cardTitle: "Consultation",
      priceNote: "incl. 20% VAT",
      duration: "approx. 60 minutes",
      zoomNote: "Initial and follow-up in-person appointments can be booked online. Zoom appointments are available by individual personal arrangement – please contact me by email or phone. Your first appointment should generally take place at the practice. If this is not possible, we can discuss a suitable solution together.",
      includesTitle: "What to expect in your first consultation",
      includes: [
        "Detailed conversation: course of complaints, previous therapies, symptoms, etc.",
        "Joint review of your existing findings (lab results, stool analyses, breath tests, medical letters)",
        "Placing possible connections between the individual complaints into context",
        "Discussion of the next sensible steps",
        "Full cost transparency: only as much as necessary, not as much as possible.",
      ],
      bookCta: "Book an appointment online",
      emailCta: "Enquire by email",
      phoneCta: "Call now",
    },
  );


  return (
    <section id="termine" className="py-12 md:py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-primary font-medium text-sm uppercase tracking-wider">{t.eyebrow}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-6">{t.heading}</h2>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Payment Notice */}
          <Card className="mb-4 border-primary/20 bg-primary/5">
            <CardContent className="p-3 flex items-center justify-center gap-2 text-sm text-foreground">
              <CreditCard className="w-4 h-4 text-primary" />
              <span>{t.payment}</span>
            </CardContent>
          </Card>

          {/* Zoom Payment Notice */}
          <Card className="mb-4 border-primary/20 bg-primary/5">
            <CardContent className="p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground text-center">
              <Video className="w-4 h-4 text-primary flex-shrink-0" />
              <span>{t.zoomNote}</span>
            </CardContent>
          </Card>

          {/* Main Pricing Card */}
          <Card className="mb-8 border-primary/20 shadow-lg">
            <CardHeader className="text-center bg-primary/5 rounded-t-lg">
              <CardTitle className="text-2xl text-foreground">{t.cardTitle}</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Euro className="w-8 h-8 text-primary" />
                  <span className="text-5xl font-bold text-foreground">139,-</span>
                </div>
                <p className="text-muted-foreground">{t.priceNote}</p>
              </div>

              <div className="flex flex-col items-center gap-2 mb-8">
                <div className="flex items-center gap-2 text-foreground">
                  <Clock className="w-5 h-5 text-primary" />
                  <span>{t.duration}</span>
                </div>
              </div>

              <div className="max-w-xl mx-auto mb-8">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-primary mb-3 text-center">
                  {t.includesTitle}
                </h3>
                <ul className="space-y-2">
                  {t.includes.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span className="leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>



              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <Link to={lp("/termin")}>
                    <CalendarCheck className="w-4 h-4 mr-2" />
                    {t.bookCta}
                  </Link>
                </Button>
                <Button variant="outline" asChild size="lg">
                  <a href={anchor("#kontakt")}>
                    <Mail className="w-4 h-4 mr-2" />
                    {t.emailCta}
                  </a>
                </Button>
                <Button variant="outline" asChild size="lg">
                  <a href="tel:+436604041993">
                    <Phone className="w-4 h-4 mr-2" />
                    {t.phoneCta}
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </section>
  );
};

export default Pricing;
